/**
 * Image Optimization Utilities
 * Handles compression and resizing before upload
 */

/**
 * Compress and optimize image before upload
 * @param {File} file - Original image file
 * @param {Object} options - Compression options
 * @returns {Promise<File>} Optimized image file
 */
export const optimizeImage = async (file, options = {}) => {
  const {
    maxWidth = 2048,
    maxHeight = 2048,
    quality = 0.85,
    format = 'jpeg',
    maintainAspectRatio = true
  } = options

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      try {
        let width = img.width
        let height = img.height

        if (maintainAspectRatio) {
          const aspectRatio = width / height
          if (width > maxWidth) {
            width = maxWidth
            height = width / aspectRatio
          }
          if (height > maxHeight) {
            height = maxHeight
            width = height * aspectRatio
          }
        } else {
          width = maxWidth
          height = maxHeight
        }

        width = Math.round(width)
        height = Math.round(height)

        canvas.width = width
        canvas.height = height

        // Fill with white background for transparent images (JPEG doesn't support transparency)
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, width, height)

        ctx.drawImage(img, 0, 0, width, height)

        const mimeType = `image/${format}`
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const optimizedFile = new File(
                [blob],
                `optimized_${file.name.split('.')[0]}.${format}`,
                { type: mimeType }
              )
              resolve(optimizedFile)
            } else {
              reject(new Error('Failed to compress image'))
            }
          },
          mimeType,
          quality
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(file)
  })
}
